import React from "react";
import { MapPin, Gauge } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { BikeDetail } from "../types/bikeTypes";
import BikeSpecifications from "./BikeSpecifications";

interface BikeDetailsTabProps {
  bike: BikeDetail;
}

const BikeDetailsTab: React.FC<BikeDetailsTabProps> = ({ bike }) => {
  const getBatteryColor = (percentage: number) => {
    if (percentage >= 70) return "text-green-500";
    if (percentage >= 30) return "text-amber-500";
    return "text-red-500";
  };

  const getStatusBadge = () => {
    switch (bike.bikeStatus) {
      case "active":
        return <Badge className="bg-green-500">Active</Badge>;
      case "inactive":
        return <Badge variant="secondary">Inactive</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  // Determine if the bike is at a user's home and format the location display accordingly
  const getLocationDisplay = () => {
    // If we have a userName from the BikeDetail, display it as a home location
    if (bike.userName) {
      return `Home: ${bike.userName}`;
    }
    
    // If we have an assigned location, display it
    if (bike.assignedLocation && bike.assignedLocation !== 'Unassigned') {
      return bike.assignedLocation;
    }
    
    // Default case if no location info is available
    return "Site: Unassigned";
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Bike Status</CardTitle>
            <CardDescription>Current information about this bike</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <span>{getStatusBadge()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Location</span>
                <div className="flex items-center">
                  <MapPin className="mr-1 h-3.5 w-3.5 text-muted-foreground" />
                  <span>{getLocationDisplay()}</span>
                </div>
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-4">
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <div className="text-sm font-medium">Battery Level</div>
                  <div className={cn("text-sm font-medium", getBatteryColor(bike.batteryStatus))}>
                    {bike.batteryStatus}%
                  </div>
                </div>
                <Progress
                  value={bike.batteryStatus}
                  className={cn("h-2", {
                    "bg-green-500": bike.batteryStatus >= 70,
                    "bg-amber-500": bike.batteryStatus >= 30 && bike.batteryStatus < 70,
                    "bg-red-500": bike.batteryStatus < 30,
                  })}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Gauge className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium">Total Mileage</div>
                  <div className="text-2xl font-bold">{bike.totalMileage} km</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <BikeSpecifications bike={bike} />
      </div>
    </div>
  );
};

export default BikeDetailsTab;
